import { View } from "react-native";
import { Button, ButtonText } from "ui/gluestack/button";
import { AddIcon, Icon } from "ui/gluestack/icon";
import { CalendarToolbar } from "../organisms/CalendarToolbar";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import dayGridPlugin from "@fullcalendar/daygrid";
import multiMonthPlugin from "@fullcalendar/multimonth";
import { useRef } from "react";
import { CalendarEventModal } from "../modals/CalendarEventModal";
import { useHandleCalendar } from "../hooks/useHandleCalendar";
import interactionPlugin, { DateClickArg } from "@fullcalendar/interaction";
import { addMinutes, format } from "date-fns";
import { PermissionCheck } from "app/features/permission/atoms/PermissionCheck";
import { PermissionsEnums } from "app/services/api/types/authorisation/enums/PermissionsEnums";
import { Portal } from "ui/modules/ThrelsPortal/Portal";

interface Props {
  locationId?: number;
  residentId?: number;
}
export function CalendarViewTemplate({ locationId, residentId }: Props) {
  const {
    calendarRef,
    currentTitle,
    switchView,
    data,
    currentView,
    handleDatesSet,
  } = useHandleCalendar({ residentId });

  const ref = useRef<React.ComponentRef<typeof CalendarEventModal>>(null);

  const toggleModal = () => {
    ref.current?.addNew();
  };

  const startAddNew = (date: DateClickArg) => {
    const day = format(date.date, "yyyy-MM-dd");
    if (date.view.type === "timeGridWeek" || date.view.type === "timeGridDay") {
      ref.current?.addNew({
        startDate: day,
        startTime: format(date.date, "HH:mm"),
        endDate: day,
        endTime: format(addMinutes(date.date, 30), "HH:mm"),
        locationId,
        residentId: locationId && residentId ? residentId : undefined,
      });
    } else {
      ref.current?.addNew({
        startDate: day,
        endDate: day,
        locationId,
        residentId: locationId && residentId ? residentId : undefined,
      });
    }
  };

  const handleEventDidMount = (info) => {
    const isCompleted = info.event.extendedProps.isCompleted;

    if (isCompleted) {
      const timeEl = info.el.querySelector(".fc-event-time");
      if (timeEl && !timeEl.querySelector(".checkmark")) {
        const checkmark = document.createElement("span");
        checkmark.textContent = "✓ ";
        checkmark.className = "checkmark";
        timeEl.insertBefore(checkmark, timeEl.firstChild);
      }
    }

    const viewType = info.view.type;
    const shouldShowTooltip = [
      "timeGridWeek",
      "dayGridMonth",
      "multiMonthYear",
    ].includes(viewType);

    if (shouldShowTooltip) {
      info.el.setAttribute("title", info.event.title);
    }
  };

  return (
    <View className="flex-1">
      {residentId ? null : (
        <Portal hostName="headerButton" identifier="calendarView">
          <PermissionCheck
            permission={PermissionsEnums.CREATE_CALENDAR_EVENT}
            fallback={<></>}
          >
            <Button
              action="primary"
              size="sm"
              className="mb-3"
              onPress={toggleModal}
            >
              <ButtonText className="text-white">
                Add new appointment
              </ButtonText>
              <Icon as={AddIcon} className="w-4 h-4 stroke-white" />
            </Button>
          </PermissionCheck>
        </Portal>
      )}

      <View className="px-dashboard flex-1">
        <View className="flex-1 bg-background-50 rounded-2xl p-4 ">
          <View className="h-full bg-white rounded-xl p-4">
            <CalendarToolbar
              currentTitle={currentTitle}
              currentView={currentView}
              switchView={switchView}
              onPrev={() => calendarRef.current?.getApi().prev()}
              onNext={() => calendarRef.current?.getApi().next()}
            />
            <FullCalendar
              ref={calendarRef}
              plugins={[
                timeGridPlugin,
                dayGridPlugin,
                multiMonthPlugin,
                interactionPlugin,
              ]}
              initialView={currentView}
              events={data}
              headerToolbar={{ left: "", center: "", right: "" }}
              noEventsText="No Events"
              eventDisplay="auto"
              height={"100%"}
              dayMaxEventRows={4}
              eventClassNames={() => "cursor-pointer overflow-hidden"}
              eventTimeFormat={{
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              }}
              dayHeaderContent={({ date, view }) => {
                if (
                  view.type === "timeGridWeek" ||
                  view.type === "timeGridDay"
                ) {
                  return format(date, "dd MMM");
                }

                // Default format for other views
                return new Intl.DateTimeFormat("en", {
                  weekday: "short",
                }).format(date);
              }}
              dateClick={startAddNew}
              allDaySlot={false}
              datesSet={handleDatesSet}
              eventClick={({ event }) => {
                ref.current?.show(event.extendedProps.id, event);
              }}
              eventDidMount={handleEventDidMount}
            />
          </View>
        </View>
      </View>
      <CalendarEventModal ref={ref} />
    </View>
  );
}
